/**
 * قالبِ توضیحِ رویداد — از متنِ کرالر تا چیزی که هر دو اپ می‌توانند رندر کنند.
 *
 * ═══ چرا اصلاً وجود دارد ═══
 *
 * سایت‌ها توضیحِ رویداد را قالب‌دار می‌نویسند: پاراگراف، خطِ تازه، چند کلمه‌ی
 * بولد، گاهی فهرستِ نقطه‌ای. کرالر تا WHOSUP-227 همه را به یک خطِ صاف تبدیل
 * می‌کرد؛ حالا قالب را تا دیتابیس می‌رساند. این فایل نیمه‌ی اپِ همان کار است
 * (WHOSUP-233) و **هیچ مایگریشنی ندارد؛ فقط رندر است.**
 *
 * ═══ دو لِن، هر دو روی همان ردیفِ `public.events` ═══
 *
 *   description              — متنِ ساده، ولی با خطِ تازه و پاراگرافِ واقعی.
 *                              همیشه هست. هیچ نشانه‌ای در آن **نشانه نیست**:
 *                              ستاره‌اش ستاره‌ی محتواست.
 *   metadata.description_rich — همان متن با مارک‌داونِ محدود. فقط وقتی هست که
 *                              چیزی به ستون اضافه کند.
 *
 * پس دو حالتِ parse داریم و نه یکی: `markdown: true` برای لِنِ دوم، و
 * `markdown: false` برای ستون. ۲۵ رویدادِ تیکت‌مستر عبارتِ «Français à suivre»
 * را خودشان بینِ دو ستاره نوشته‌اند — در نسخه‌ی قالب‌دار escape شده می‌آیند و
 * در ستونِ ساده عیناً همان دو ستاره. اگر ستون هم مارک‌داون parse می‌شد، همان
 * عبارت روی ۲۵ رویداد ایتالیک می‌شد و ستاره‌هایش گم.
 *
 * ═══ زیرمجموعه‌ی مارک‌داون بسته است ═══
 *
 * خطِ تازه و مرزِ پاراگراف · `**بولد**` · `*ایتالیک*` · `- ` فهرست ·
 * `\*` ستاره‌ی escape شده. همین. مشخصاً:
 *
 * ⚠ **HTML هرگز نمی‌آید و این‌جا هم هیچ‌وقت HTML بیرون نمی‌دهد.** خروجی یک
 *   ساختارِ داده است، نه رشته‌ی نشانه‌دار؛ هر دو اپ از آن کامپوننت می‌سازند.
 *   مارک‌داونِ محدود دقیقاً برای همین انتخاب شد که سؤالِ «این HTML امن است؟»
 *   درباره‌ی متنی که از سایتِ غریبه می‌آید اصلاً پرسیده نشود.
 *
 * ⚠ **لینک عمداً بیرون است**: متنش می‌ماند، آدرسش نه. وگرنه قاعده‌ی «به رقیب
 *   لینک نمی‌دهیم» از راهِ متنِ توضیح دور زده می‌شود.
 *
 * ⚠ نشانه‌ای که جفتِ بسته ندارد، **متن** است نه نشانه. یک ستاره‌ی تنها وسطِ
 *   جمله باید همان ستاره دیده شود، نه اینکه بقیه‌ی پاراگراف را ایتالیک کند.
 */

/** یک تکه متنِ یکدست داخلِ یک خط. */
export interface RichSpan {
  text: string;
  bold?: boolean;
  italic?: boolean;
}

/**
 * یک بلوک: یا پاراگراف یا فهرستِ نقطه‌ای.
 *
 * `lines` برای پاراگراف یعنی خط‌های داخلش (شکستِ ساده، بدونِ فاصله‌ی پاراگراف)
 * و برای فهرست یعنی آیتم‌هایش. یک شکل برای هر دو، تا رندرِ هر اپ یک حلقه
 * بماند نه دو تا.
 */
export interface RichBlock {
  kind: 'p' | 'ul';
  lines: RichSpan[][];
}

const isStar = (c: { c: string; lit: boolean } | undefined): boolean =>
  !!c && c.c === '*' && !c.lit;

/** طولِ رشته‌ی ستاره‌های پشتِ سرِ هم از این نقطه. */
const starRun = (chars: { c: string; lit: boolean }[], from: number): number => {
  let n = 0;
  while (isStar(chars[from + n])) n++;
  return n;
};

/**
 * جفتِ بسته‌ی این نشانه کجاست؟ `-1` یعنی ندارد.
 *
 * طولِ رشته‌ی بسته باید **دقیقاً** برابرِ بازکننده باشد. اگر `>=` بود، در
 * «‎5 * 3 seats and **note**‎» آن ستاره‌ی ضرب با ستاره‌ی اولِ بولد جفت می‌شد و
 * نصفِ جمله ایتالیک درمی‌آمد.
 */
const findCloser = (
  chars: { c: string; lit: boolean }[], from: number, end: number, len: number,
): number => {
  for (let i = from; i < end; i++) {
    if (!isStar(chars[i])) continue;
    const n = starRun(chars, i);
    // `i > from` یعنی بینِ باز و بسته چیزی هست؛ `**‎**` نشانه نیست.
    if (n === len && i > from) return i;
    i += n - 1;
  }
  return -1;
};

/**
 * یک بازه از خط → تکه‌ها، با قالبِ ارث‌رسیده.
 *
 * جفت‌ها بازگشتی حل می‌شوند و نه با یک کلیدِ روشن/خاموش: با کلید، نشانه‌ای که
 * تا آخرِ خط بسته نشد قالبش را تا ته می‌کشاند. این‌جا نشانه فقط وقتی نشانه
 * می‌شود که جفتش **از قبل پیدا شده باشد**؛ وگرنه ستاره‌ها خودشان متن‌اند.
 */
const walk = (
  chars: { c: string; lit: boolean }[], start: number, end: number,
  bold: boolean, italic: boolean, out: RichSpan[],
): void => {
  let buf = '';
  const flush = (): void => {
    if (!buf) return;
    const span: RichSpan = { text: buf };
    if (bold) span.bold = true;
    if (italic) span.italic = true;
    out.push(span);
    buf = '';
  };

  let i = start;
  while (i < end) {
    const ch = chars[i]!;
    if (!isStar(ch)) { buf += ch.c; i++; continue; }

    const run = starRun(chars, i);
    // سه ستاره و بیشتر بیرونِ زیرمجموعه است — متن حسابش می‌کنیم.
    const close = run <= 2 ? findCloser(chars, i + run, end, run) : -1;
    if (close < 0) { buf += '*'.repeat(run); i += run; continue; }

    flush();
    walk(chars, i + run, close, bold || run === 2, italic || run === 1, out);
    i = close + run;
  }
  flush();
};

/**
 * یک خطِ مارک‌داون → تکه‌های قالب‌دار.
 *
 * اول `\*` به یک کاراکترِ «ستاره‌ی محتوایی» تبدیل می‌شود (`lit: true`) تا از
 * آن به بعد هیچ‌جا نشانه حساب نشود.
 */
const inlineMarkdown = (src: string): RichSpan[] => {
  const chars: { c: string; lit: boolean }[] = [];
  for (let i = 0; i < src.length; i++) {
    if (src[i] === '\\' && src[i + 1] === '*') {
      chars.push({ c: '*', lit: true });
      i++;
    } else {
      chars.push({ c: src[i]!, lit: false });
    }
  }
  const out: RichSpan[] = [];
  walk(chars, 0, chars.length, false, false, out);
  return out;
};

/** یک خطِ ساده → یک تکه‌ی بی‌قالب. هیچ نشانه‌ای خوانده نمی‌شود. */
const inlinePlain = (src: string): RichSpan[] => (src ? [{ text: src }] : []);

/** `- متن` در ابتدای خط → خودِ متن؛ وگرنه `null`. */
const bulletOf = (line: string): string | null => {
  const m = /^\s*-\s+(.*)$/.exec(line);
  return m ? m[1]!.trim() : null;
};

/**
 * متن → بلوک‌های آماده‌ی رندر.
 *
 * `markdown` فقط برای `metadata.description_rich` درست است؛ برای ستونِ
 * `description` باید `false` بماند (دلیلش بالای فایل).
 */
export const parseRichText = (
  text: string | null | undefined, markdown = false,
): RichBlock[] => {
  if (!text) return [];
  const inline = markdown ? inlineMarkdown : inlinePlain;
  const blocks: RichBlock[] = [];
  let cur: RichBlock | null = null;

  for (const raw of text.replace(/\r\n?/g, '\n').split('\n')) {
    const line = raw.trim();
    if (!line) { cur = null; continue; }

    const bullet = markdown ? bulletOf(line) : null;
    const kind: RichBlock['kind'] = bullet === null ? 'p' : 'ul';
    // فهرست و پاراگرافِ چسبیده به هم دو بلوک‌اند، حتی بدونِ خطِ خالی بینشان.
    if (!cur || cur.kind !== kind) {
      cur = { kind, lines: [] };
      blocks.push(cur);
    }
    const spans = inline(bullet ?? line);
    if (spans.length) cur.lines.push(spans);
  }

  return blocks.filter((b) => b.lines.length > 0);
};

/**
 * `metadata` خام → نسخه‌ی قالب‌دارِ توضیح، یا `null`.
 *
 * ⚠ محافظه‌کارانه، مثلِ parseAlternatives: `metadata` قراردادی است که مخزنِ
 * دیگری می‌نویسد و یک شکلِ غیرمنتظره باید یعنی «همان ستون را نشان بده»، نه
 * یک خطا.
 */
export const richDescription = (metadata: unknown): string | null => {
  if (typeof metadata !== 'object' || metadata === null || Array.isArray(metadata)) return null;
  const raw = (metadata as Record<string, unknown>).description_rich;
  return typeof raw === 'string' && raw.trim() ? raw : null;
};

/**
 * توضیحِ یک آیتم، به بهترین شکلی که برایش داریم.
 *
 * قاعده یک خط است: نسخه‌ی قالب‌دار بود؟ همان؛ وگرنه ستون. جمله‌ی «رویداد لغو
 * شد» در هر دو لِن نوشته می‌شود (publisher نسخه ۳۰)، پس انتخابِ هر کدام امن
 * است و آن هشدار گم نمی‌شود.
 */
export const descriptionBlocks = (
  item: { description?: string | null; descriptionRich?: string | null },
): RichBlock[] => {
  const rich = item.descriptionRich?.trim();
  return rich ? parseRichText(rich, true) : parseRichText(item.description, false);
};
